"use client";

import { useState, useEffect } from "react";
import { getApiEndpoint } from "@/config/api";

export interface Partner {
  image: string;
  name: string;
  description: string;
  category: string[];
}

interface PaginationMeta {
  page: number;
  perPage: number;
  pageCount: number;
  total: number;
}

interface PartnersResponse {
  data: Partner[];
  meta: {
    pagination: PaginationMeta;
  };
}

async function fetchPartners(
  page: number,
  perPage: number
): Promise<{ data: Partner[]; hasMore: boolean }> {
  const url = `${getApiEndpoint("partners")}?page=${page}&perPage=${perPage}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error("Failed to fetch partners");
  }
  const result: PartnersResponse = await response.json();
  const hasMore = result.meta.pagination.page < result.meta.pagination.pageCount;
  return { data: result.data, hasMore };
}

export function usePartners() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 10;

  useEffect(() => {
    const loadPartners = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchPartners(1, pageSize);
        setPartners(result.data);
        setHasMore(result.hasMore);
      } catch (err) {
        console.error("Error fetching partners:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch partners");
        setPartners([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    };

    loadPartners();
  }, []);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    setError(null);
    try {
      const nextPage = page + 1;
      const result = await fetchPartners(nextPage, pageSize);
      setPartners((prev) => [...prev, ...result.data]);
      setPage(nextPage);
      setHasMore(result.hasMore);
    } catch (err) {
      console.error("Error loading more partners:", err);
      setError(err instanceof Error ? err.message : "Failed to load more partners");
    } finally {
      setLoadingMore(false);
    }
  };

  return {
    partners,
    loading,
    loadingMore,
    error,
    loadMore,
    hasMore,
  };
}
